'use client'

import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useRouter } from 'next/navigation'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { MarkdownEditor } from '@/components/ui/markdown-editor'
import { useToast } from '@/hooks/use-toast'
import {
  Newspaper,
  Plus,
  X,
  Loader2
} from 'lucide-react'
import { useAuthStore } from '@/lib/stores/auth-store'
import { getRedmineBaseUrl } from '@/lib/tenant-config'

interface AddNewsSidebarProps {
  projectId: string
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export function AddNewsSidebar({ projectId, isOpen, onClose, onSuccess }: AddNewsSidebarProps) {
  const [title, setTitle] = useState('')
  const [summary, setSummary] = useState('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [mounted, setMounted] = useState(false)
  const { session } = useAuthStore()
  const { toast } = useToast()
  const router = useRouter()

  useEffect(() => {
    setMounted(true)
  }, [])

  const getAuthHeaders = () => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (session?.credentials?.basicAuth) {
      headers['Authorization'] = `Basic ${session.credentials.basicAuth}`
    } else if (session?.apiKey) {
      headers['X-Redmine-API-Key'] = session.apiKey
    }

    const baseUrl = getRedmineBaseUrl()
    headers['X-Redmine-Base-URL'] = baseUrl

    return headers
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!title.trim()) {
      toast({
        variant: 'destructive',
        title: 'Title Required',
        description: 'Please enter a title for your news post',
      })
      return
    }

    if (!description.trim()) {
      toast({
        variant: 'destructive',
        title: 'Description Required',
        description: 'Please enter a description for your news post',
      })
      return
    }

    try {
      setSubmitting(true)

      const authHeaders = getAuthHeaders()

      const response = await fetch(`/api/projects/${projectId}/news`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          news: {
            title,
            summary,
            description
          }
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        
        // Handle different error types
        if (response.status === 401) {
          toast({
            variant: 'destructive',
            title: 'Authentication Failed',
            description: 'You are not authenticated. Please log in and try again.',
          })
        } else if (response.status === 403) {
          toast({
            variant: 'destructive',
            title: 'Permission Denied',
            description: 'You do not have permission to create news posts in this project.',
          })
        } else if (response.status === 404) {
          toast({
            variant: 'destructive',
            title: 'Project Not Found',
            description: 'The specified project could not be found.',
          })
        } else if (response.status === 422) {
          toast({
            variant: 'destructive',
            title: 'Validation Error',
            description: errorData.details || errorData.error || 'Please check your input and try again.',
          })
        } else {
          toast({
            variant: 'destructive',
            title: 'Failed to Create News',
            description: errorData.error || errorData.details || 'An unexpected error occurred. Please try again.',
          })
        }
        return
      }

      // Success - get the created news data
      const newsData = await response.json()
      const newsId = newsData.news?.id
      
      // Show success toast
      toast({
        title: 'News Post Created',
        description: 'Your news post has been published successfully.',
      })

      // Reset form
      setTitle('')
      setSummary('')
      setDescription('')

      onSuccess()
      onClose()

      // Redirect to news details page if we have the news ID
      if (newsId) {
        setTimeout(() => {
          router.push(`/news/${newsId}`)
        }, 500)
      }
    } catch (error) {
      console.error('News creation error:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      toast({
        variant: 'destructive',
        title: 'Error Creating News',
        description: errorMessage,
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (!isOpen) return null
  if (!mounted) return null

  const sidebarContent = (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-[100]"
        onClick={onClose}
        style={{ margin: 0 }}
      />

      {/* Sidebar */}
      <div
        className="fixed right-0 top-0 bottom-0 w-full md:w-[500px] bg-background shadow-2xl z-[101] flex flex-col"
        style={{ margin: 0 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="rounded-full p-2 bg-primary/10">
              <Newspaper className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Create News Post</h2>
              <p className="text-sm text-muted-foreground">Share updates with your team</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Title */}
          <div className="space-y-2">
            <label className="text-sm font-semibold">
              Title <span className="text-red-500">*</span>
            </label>
            <Input
              placeholder="Enter news title..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="text-base"
            />
          </div>

          {/* Summary */}
          <div className="space-y-2">
            <label className="text-sm font-semibold">Summary</label>
            <Input
              placeholder="Brief summary (optional)"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              className="text-base"
            />
            <p className="text-xs text-muted-foreground">A short description shown in the news list</p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label className="text-sm font-semibold">Description</label>
            <MarkdownEditor
              value={description}
              onChange={setDescription}
              placeholder="Write your news article content..."
              height={400}
            />
            <p className="text-xs text-muted-foreground">Supports Markdown formatting</p>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 pb-8 border-t bg-muted/30 flex-shrink-0">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="mr-2 h-4 w-4" />
                Create Post
              </>
            )}
          </Button>
        </div>
      </div>
    </>
  )

  return createPortal(sidebarContent, document.body)
}
